import * as w from '@packages/systems/wfdl';
import * as wast from '@packages/systems/wfdl-ast';
import * as wq from '@packages/systems/wfdl-Qualified';
import type {Form} from '@packages/systems/flows/types';
import {TOption} from '@packages/systems/plugins/Option';
import {TOptionalEmail, TEmail} from '@packages/systems/plugins/Email';
import {TOptionalNumber} from '@packages/systems/plugins/Number';
import {TOptionalLink, TLink} from '@packages/systems/plugins/Link';
import {TNonEmptyText, TOptionalText} from '@packages/systems/plugins/Text';
import {TPhone, TOptionalPhone} from '@packages/systems/plugins/Phone';
import {TBoolean} from '@packages/systems/plugins-utils/Boolean';
import {weakMemo} from '@packages/utilities/memo';

const TBooleanPluginType = wast.TTypeConstructor(
  wq.qualify(wq.pluginName('Boolean'), wq.typeName('Boolean'))
);

/**
 * Creates a union type from an array of strings representing radio and select field choices.
 */
export const choicesToType = weakMemo((choices: Array<string>) =>
  wast.TUnion(
    choices.reduce<Record<string, any>>((result, choice) => {
      result[choice] = wast.TEmptyRecord;
      return result;
    }, {})
  )
);

export function formFieldToType(form: Form): {
  [key: string]: w.Type;
} {
  const output = form.fields.reduce<Record<string, any>>((acc, field) => {
    let value = null;

    if (field.fieldType.tag === 'Plain') {
      value = wast.TText;
    }

    if (field.fieldType.tag === 'Email') {
      value = wast.TTypeConstructor(
        wq.qualify(wq.pluginName('Email'), wq.typeName('Email'))
      );
    }

    // Note: In general, password fields are only available on USYS pages
    // (login, signup, update, reset) and password protected pages. Forms
    // aren't actually allowed to be submitted if there is a password
    // field anywhere else, so we exclude being able to bind to a password
    // field.
    //
    // if (field.fieldType.tag === 'Password') {
    //   value = wast.TText;
    // }

    if (field.fieldType.tag === 'Phone') {
      value = wast.TTypeConstructor(
        wq.qualify(wq.pluginName('Phone'), wq.typeName('Phone'))
      );
    }

    if (field.fieldType.tag === 'Number') {
      value = wast.TNumber;
    }

    if (field.fieldType.tag === 'Checkbox') {
      value = TBooleanPluginType;
    }

    if (field.fieldType.tag === 'Select') {
      if (field.fieldType.val.allowMultiple) {
        value = wast.TRecord(
          field.fieldType.val.choices.reduce<Record<string, any>>(
            (result, choice) => {
              result[choice] = TBooleanPluginType;
              return result;
            },
            {}
          )
        );
      } else {
        value = choicesToType(field.fieldType.val.choices);
      }
    }

    if (field.fieldType.tag === 'Radio') {
      value = choicesToType(field.fieldType.val.choices);
    }

    if (field.fieldType.tag === 'File') {
      value = wast.TTypeConstructor(
        wq.qualify(wq.pluginName('Link'), wq.typeName('Link'))
      );
    }

    if (value) {
      // Checkboxes are never optional, since keeping them unchecked
      // is equivalent to the value being "false"
      if (field.fieldType.tag === 'Checkbox') {
        acc[field.fieldName] = value;
      }
      // For every other field, checks the field settings to determine if optional
      else {
        acc[field.fieldName] = field.required ? value : TOption(value);
      }
    }

    return acc;
  }, {});

  return output;
}

export function formFieldsToUiType(form: Form) {
  return form.fields.reduce<Record<string, any>>((acc, field) => {
    const value = {
      initialState: field.required
        ? wast.EHole()
        : wast.EUnion({tag: 'None', value: wast.ERecord({})}),
      type: undefined,
      required: field.required,
    } as const;

    if (field.fieldType.tag === 'Plain') {
      // @ts-expect-error - TS2540 - Cannot assign to 'type' because it is a read-only property.
      value.type = field.required ? TNonEmptyText : TOptionalText;
    }

    if (field.fieldType.tag === 'Email') {
      // @ts-expect-error - TS2540 - Cannot assign to 'type' because it is a read-only property.
      value.type = field.required ? TEmail : TOptionalEmail;
    }

    if (field.fieldType.tag === 'Phone') {
      // @ts-expect-error - TS2540 - Cannot assign to 'type' because it is a read-only property.
      value.type = field.required ? TPhone : TOptionalPhone;
    }

    if (field.fieldType.tag === 'Number') {
      // @ts-expect-error - TS2540 - Cannot assign to 'type' because it is a read-only property.
      value.type = field.required ? wast.TNumber : TOptionalNumber;
    }

    if (field.fieldType.tag === 'Checkbox') {
      // @ts-expect-error - TS2540 - Cannot assign to 'initialState' because it is a read-only property.
      value.initialState = wast.EUnion({
        tag: 'False',
        value: wast.ERecord({}),
      });
      // @ts-expect-error - TS2540 - Cannot assign to 'type' because it is a read-only property.
      value.type = TBoolean;
    }

    if (field.fieldType.tag === 'Select') {
      const {choices} = field.fieldType.val;

      /**
       * The allowMultiple allows the user to
       * select none to many. The state is initialized
       * to False on every choice to ensure that the none
       * case is possible for both required and
       * non-required attribute on the element.
       */
      if (field.fieldType.val.allowMultiple) {
        // @ts-expect-error - TS2540 - Cannot assign to 'initialState' because it is a read-only property.
        value.initialState = wast.ERecord(
          choices.reduce<Record<string, any>>((result, choice) => {
            if (choice) {
              result[choice] = wast.EUnion({
                tag: 'False',
                value: wast.ERecord({}),
              });
            }
            return result;
          }, {})
        );

        // @ts-expect-error - TS2540 - Cannot assign to 'type' because it is a read-only property.
        value.type = w.withSystem(
          {labelVariant: 'hidden'},
          wast.TRecord(
            choices.reduce<Record<string, any>>((result, choice) => {
              if (choice) {
                result[choice] = TBoolean;
              }
              return result;
            }, {})
          )
        );
      } else {
        const choiceTypes = wast.TUnion(
          choices.reduce<Record<string, any>>((result, choice) => {
            if (choice) {
              result[choice] = wast.TEmptyRecord;
            }
            return result;
          }, {})
        );

        // @ts-expect-error - TS2540 - Cannot assign to 'type' because it is a read-only property.
        value.type = w.withSystem(
          {labelVariant: 'hidden'},
          // Wrap elements with multiple options in a TOption
          field.required ? choiceTypes : TOption(choiceTypes)
        );
      }
    }

    if (field.fieldType.tag === 'Radio') {
      const choiceTypes = wast.TUnion(
        field.fieldType.val.choices.reduce<Record<string, any>>(
          (result, choice) => {
            result[choice] = wast.TEmptyRecord;
            return result;
          },
          {}
        )
      );

      // @ts-expect-error - TS2540 - Cannot assign to 'type' because it is a read-only property.
      value.type = w.withSystem(
        {labelVariant: 'hidden'},
        // Wrap elements with multiple options in a TOption
        field.required ? choiceTypes : TOption(choiceTypes)
      );
    }

    if (field.fieldType.tag === 'File') {
      // @ts-expect-error - TS2540 - Cannot assign to 'type' because it is a read-only property.
      value.type = field.required ? TLink : TOptionalLink;
    }

    if (value.type && value.initialState) {
      acc[field.fieldName] = value;
    }

    return acc;
  }, {});
}
